import React from 'react';
import styled from 'styled-components';
import { Experience, useExperienceApi } from '@/webapi/use-experience-api';
import { VSpace } from '@/components/spacing';
import {
  GoalChip,
  GoalsWrapper,
} from '@/features/editor/widgets/shared/inline-variants-layout/goals';
import { breakpoints } from '@/components/responsive';
import { MetricKind } from '@/webapi/models';
import { scrollToSelector } from '@/utils/browser';
import {
  allPriceTestGoals,
  defaultInitialPriceTestGoals,
  formatPriceTestGoal,
} from '@/features/details/price-test-reports/shared';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';

interface MoreMetricsProps {
  experience: Experience;
  setExperience?: (e: Experience) => void;
}

export const MetricsList = styled(GoalsWrapper)`
  && {
    margin: 0 auto;
    max-width: 100rem;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 70%;
  margin: 0 auto;
`;

export function MoreMetrics({ experience, setExperience }: MoreMetricsProps) {
  const metricKinds = allPriceTestGoals().filter(
    (g) =>
      ![...(experience.goals || defaultInitialPriceTestGoals())].includes(g),
  );
  const { loading, setExperienceGoals } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  return (
    <Container>
      <VSpace value={4} />
      <Title>Would like to see more reports?</Title>
      <MetricsList>
        {metricKinds.map((metric) => {
          const onClick = async () => {
            if (loading) {
              return;
            }
            const cloned = clone(experience) as Experience;
            cloned.goals = cloned.goals || [];
            cloned.goals.push(metric);
            await setExperienceGoals(
              experience.primaryGoal || MetricKind.PRODUCT_CR,
              cloned.goals,
            );
            setExperience(cloned);
            scrollToSelector(`div[data-title="${metric}"]`);
          };
          return (
            <Chip key={metric} onClick={onClick}>
              {formatPriceTestGoal(metric)}
            </Chip>
          );
        })}
      </MetricsList>
    </Container>
  );
}

const Chip = styled(GoalChip)`
  && {
    background-color: #cfd2d9;
    color: #0c0c0c;
    margin: 1rem;
    font-weight: 700;
    font-size: 1.4rem;
    border: none;
    text-transform: uppercase;
  }
`;

const Title = styled.p`
  user-select: none;
  margin: 0 auto 4rem auto;
  color: #b8c2c9;
  letter-spacing: 0.48px;
  font-family: Inter, serif;
  font-weight: 800;
  font-size: 2.5rem;
  text-align: center;

  ${breakpoints.down(`md`)} {
    font-size: 1.8rem;
  }
`;
